import { useState, useEffect } from 'react';
import { TimeSlot, Supplement } from '@/types/supplement';

// Demo supplement data that works without authentication
const DEMO_SUPPLEMENTS: Supplement[] = [
  {
    id: 'demo-1',
    name: 'Vitamin D3',
    brand: 'Premium Health',
    dosage: '2000 IU',
    timing: 'morning',
    suggestedTime: '08:00',
    icon: '☀️',
    taken: false,
  },
  {
    id: 'demo-2', 
    name: 'Omega-3 Fish Oil',
    brand: 'Pure Marine',
    dosage: '1000mg',
    timing: 'morning',
    suggestedTime: '08:00',
    icon: '🐟',
    taken: false,
  },
  {
    id: 'demo-3',
    name: 'Magnesium',
    brand: 'Calm & Restore',
    dosage: '400mg',
    timing: 'evening',
    suggestedTime: '19:00',
    icon: '🌙',
    taken: false,
  }
];

export const useDemoSupplements = () => {
  const [supplements, setSupplements] = useState<Supplement[]>(() => {
    // Load from localStorage if available
    const stored = localStorage.getItem('demo-supplements');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.warn('Failed to parse stored supplements');
      }
    }
    return DEMO_SUPPLEMENTS;
  });

  // Save to localStorage whenever supplements change
  useEffect(() => {
    localStorage.setItem('demo-supplements', JSON.stringify(supplements));
  }, [supplements]);

  const createTimeSlots = (supplements: Supplement[]): TimeSlot[] => {
    const supplementsByTiming = supplements.reduce((acc, sup) => {
      if (!acc[sup.timing]) {
        acc[sup.timing] = [];
      }
      acc[sup.timing].push(sup);
      return acc;
    }, {} as Record<string, Supplement[]>);

    const timeSlots: TimeSlot[] = [];

    if (supplementsByTiming.morning) {
      timeSlots.push({
        id: 'morning',
        label: 'Morning',
        timeRange: '08:00 - 10:00',
        startHour: 8,
        endHour: 10,
        completed: supplementsByTiming.morning.every(s => s.taken),
        supplements: supplementsByTiming.morning,
      });
    }

    if (supplementsByTiming.afternoon) {
      timeSlots.push({
        id: 'afternoon',
        label: 'Afternoon',
        timeRange: '13:00 - 15:00',
        startHour: 13,
        endHour: 15,
        completed: supplementsByTiming.afternoon.every(s => s.taken),
        supplements: supplementsByTiming.afternoon,
      });
    }

    if (supplementsByTiming.evening) {
      timeSlots.push({
        id: 'evening',
        label: 'Evening',
        timeRange: '19:00 - 21:00',
        startHour: 19,
        endHour: 21,
        completed: supplementsByTiming.evening.every(s => s.taken),
        supplements: supplementsByTiming.evening,
      });
    }

    if (supplementsByTiming.anytime) {
      timeSlots.push({
        id: 'anytime',
        label: 'Anytime',
        timeRange: 'Throughout the day',
        startHour: 0,
        endHour: 24,
        completed: supplementsByTiming.anytime.every(s => s.taken),
        supplements: supplementsByTiming.anytime,
      });
    }

    return timeSlots;
  };

  const timeSlots = createTimeSlots(supplements);

  const handleSupplementToggle = (supplementId: string) => {
    setSupplements(prev => 
      prev.map(sup => 
        sup.id === supplementId 
          ? { 
              ...sup, 
              taken: !sup.taken,
              actualTakenTime: !sup.taken ? new Date().toISOString() : undefined
            }
          : sup
      )
    );
  };

  const handleQuickComplete = () => {
    const now = new Date().toISOString();
    setSupplements(prev => 
      prev.map(sup => ({ 
        ...sup, 
        taken: true, 
        actualTakenTime: now 
      }))
    );
  };

  const addSupplement = (supplement: Omit<Supplement, 'id' | 'taken'>) => {
    const newSupplement: Supplement = {
      ...supplement,
      id: `demo-${Date.now()}`,
      taken: false,
    };
    setSupplements(prev => [...prev, newSupplement]);
  };

  const removeSupplement = (supplementId: string) => {
    setSupplements(prev => prev.filter(s => s.id !== supplementId));
  };

  const totalSupplements = supplements.length;
  const completedSupplements = supplements.filter(s => s.taken).length;
  const allCompleted = completedSupplements === totalSupplements;
  const remainingCount = totalSupplements - completedSupplements;

  return {
    supplements,
    timeSlots,
    handleSupplementToggle,
    handleQuickComplete,
    addSupplement,
    removeSupplement,
    totalSupplements,
    completedSupplements,
    allCompleted,
    remainingCount
  };
};